<script lang="ts" setup>
import { computed, ref, watch, type PropType } from 'vue';

import { DivTableColumn, divTableSortDirections } from './div-table-helpers';
import DivTableHeader from './DivTableHeader.vue';

const props = defineProps({
  header: { type: Array as PropType<Array<DivTableColumn<unknown>>>, default: () => [] },
  rows: { type: Array as PropType<Array<unknown>>, default: () => [] },
  stickyHeader: { type: Boolean, default: false },
});
const emit = defineEmits<{
  (e: 'header-changed', value: Array<DivTableColumn<unknown>>): void;
}>();

const internalHeader = ref(props.header);

watch(
  () => props.header,
  () => {
    internalHeader.value = props.header;
  },
);

const sortedColumn = computed(() => internalHeader.value?.find((col) => col.sorted));
const sortedRows = computed(() => {
  if (sortedColumn.value === undefined || !sortedColumn.value.sortable) {
    return props.rows;
  }

  const ascSortedRows = props.rows.map((x) => x).sort((a, b) => sortedColumn.value?.sortFunction?.(a, b) || -1);

  return sortedColumn.value?.sortDirection === divTableSortDirections.asc ? ascSortedRows : ascSortedRows.reverse();
});
</script>

<template>
  <div class="div-table">
    <div-table-header
      v-if="props.header"
      v-model:value="internalHeader"
      class="x-div-table__header"
      :sticky-header="props.stickyHeader"
      @input="emit('header-changed', $event)"
    />
    <slot :rows="sortedRows" />
  </div>
</template>

<style lang="scss">
.div-table {
  --number-display-font-weight: var(--font-weight-regular);

  display: grid;
  width: 100%;

  .div-table__body {
    display: grid;
    overflow: hidden;
    color: rgb(var(--v-theme-text-secondary));
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);

    .xs & {
      font-size: var(--font-size-md);
    }
  }
}
</style>
